import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import SearchBar from "./SearchBar";
import Label from './form/Label';

const GenericDropdown = ({
    heading,
    labelComponent,
    tooltip,
    options = [],
    valueKey = 'value',
    displayValueKey = 'displayValue',
    defaultValue = '',
    placeholder = '--Select--',
    displaySelectedValue = true,
    addEmptyOption = false,
    emptyOptionPlaceholder = null,
    displayValue,
    onSelect = () => { },
    renderPostItem = () => null,
    grow = false,
    readOnly = false,
    multiple = false,
    size,
    maxWidth,
    buttonTooltip = false,
    isRequired = false,
    toggleVariant = 'white',
    showSearchBar = false,
    maxOptionsDisplayed = 15,
    id,
    name,
    css = {},
}) => {
    const ref = useRef();
    const [translate] = useTranslation();
    const [selectedValue, setSelectedValue] = useState(defaultValue);
    const [searchPrompt, setSearchPrompt] = useState('');

    useEffect(() => {
        setSelectedValue(defaultValue);
    }, [defaultValue]);

    const allOptions = useMemo(() => {
        const result = options.map((option) => option);

        if (addEmptyOption) {
            result.unshift({
                [valueKey]: '',
                [displayValueKey]: emptyOptionPlaceholder ?? placeholder,
            });
        }

        return result;
    }, [addEmptyOption, emptyOptionPlaceholder, valueKey, displayValueKey, placeholder, options]);

    const getOptionDisplay = useCallback((value) => {
        const option = allOptions.find((option) => option[valueKey] === value);

        return (option && option[displayValueKey]) || value;
    }, [allOptions, valueKey, displayValueKey]);

    const selectedDisplayValue = useMemo(() => {
        if (!displaySelectedValue) {
            return displayValue || getOptionDisplay('') || placeholder;
        }

        let result;

        if (selectedValue !== undefined && selectedValue !== null) {
            result = multiple ? selectedValue.map(value => getOptionDisplay(value)).join(", ") : getOptionDisplay(selectedValue);
        }
        return result || getOptionDisplay('') || placeholder;
    }, [displaySelectedValue, getOptionDisplay, selectedValue, multiple, placeholder, displayValue]);

    const handleChange = useCallback((eventKey) => {
        if (!multiple) {
            setSelectedValue(eventKey);
            onSelect(eventKey);
        }
        else {
            let newValue = [];
            const index = selectedValue.findIndex(x => x === eventKey);
            if (index === -1) {
                newValue = [...selectedValue, eventKey];
            }
            else {
                newValue = selectedValue;
                newValue.splice(index, 1);
            }
            onSelect([...newValue]);

            setSelectedValue([...newValue]);
        }
    }, [multiple, selectedValue, setSelectedValue, onSelect]);

    const toggle = useMemo(() => {
        return (
            <Dropdown.Toggle
                variant={toggleVariant}
                size={size}
                className={`border ${css.toggle || ''} ${grow ? 'd-flex justify-content-between align-items-center flex-grow-1' : ''} overflow-hidden`}
                disabled={readOnly || allOptions.length === (addEmptyOption ? 1 : 0)}
                style={{ width: maxWidth }}
            >
                {selectedDisplayValue}
            </Dropdown.Toggle>
        );
    }, [selectedDisplayValue, readOnly, allOptions, size, maxWidth, grow, toggleVariant, css.toggle, addEmptyOption])

    const filterFunction = useCallback(option => {
        if (searchPrompt) {
            return option[displayValueKey]?.toLowerCase().includes(searchPrompt.toLowerCase())
        }
        return true;
    }, [displayValueKey, searchPrompt]);

    const dropdownOptions = useMemo(() => {
        return (
            <Dropdown.Menu className={`${css.menu ?? ""} ${grow ? "w-100" : ""}`}>
                {showSearchBar && <div className='pb-2 px-2'>
                    <SearchBar ref={ref} size="sm" initialValue={searchPrompt} placeholder={translate("OTM_SEARCH")} onChange={(value) => setSearchPrompt(value)} />
                </div>}
                {allOptions.filter(filterFunction).map((option, index) => {
                    if (showSearchBar && index >= maxOptionsDisplayed) {
                        return null;
                    }

                    if (option.isDivider) {
                        return <Dropdown.Divider key={index} />
                    }

                    const value = option[valueKey];
                    const displayValue = getOptionDisplay(value);

                    return (
                        <Dropdown.Item
                            key={index}
                            eventKey={value}
                            active={multiple ? selectedValue.includes(value) : selectedValue === value}
                            className={`${size === "sm" ? "small" : ""} ${css.item ?? ""}`}
                        >
                            <span>{'' + displayValue}</span>
                            {renderPostItem({ option, value, displayValue, selectedValue, index })}
                        </Dropdown.Item>
                    );
                })}
            </Dropdown.Menu>
        );
    }, [css.menu, css.item, multiple, allOptions, maxOptionsDisplayed, valueKey, selectedValue, searchPrompt, showSearchBar, grow, size,
        filterFunction, translate, getOptionDisplay, renderPostItem]);

    const dropdown = useMemo(() => {
        return (
            <Dropdown
                onSelect={handleChange}
                id='dropdown'
                className={`${css.dropdown || ''} ${grow ? 'd-flex' : ''}`}
                autoClose={multiple ? "outside" : true}
            >
                {toggle}
                {dropdownOptions}
            </Dropdown>
        );
    }, [handleChange, toggle, dropdownOptions, multiple, grow, css.dropdown]);

    return (
        <>
            <Label
                controlId={id || name}
                label={heading}
                labelComponent={labelComponent}
                tooltip={tooltip}
                buttonTooltip={buttonTooltip}
                isRequired={isRequired}
            />
            {dropdown}
        </>
    );
};

export default GenericDropdown;